import type { ContactStage, ContactStageEntry } from "./contacts";

const sortByChangedAtAsc = (entries: ContactStageEntry[]): ContactStageEntry[] =>
  [...entries].sort((a, b) => a.changedAt.getTime() - b.changedAt.getTime());

/**
 * Ensures a non-empty history array for reads and UI. Legacy contacts without
 * `stageHistory` / `inventoryStageHistory` get a single synthetic entry at
 * `fallbackChangedAt`.
 */
export function normalizeContactStageHistory(
  history: ContactStageEntry[] | undefined,
  fallbackStage: ContactStage,
  fallbackChangedAt: Date,
): ContactStageEntry[] {
  const sorted = sortByChangedAtAsc(history ?? []);
  if (sorted.length > 0) return sorted;
  return [{ stage: fallbackStage, changedAt: fallbackChangedAt }];
}

/** Latest stage in history, or `fallback` when empty. */
export function getCurrentContactStage(
  history: ContactStageEntry[] | undefined,
  fallback: ContactStage,
): ContactStage {
  const sorted = sortByChangedAtAsc(history ?? []);
  return sorted[sorted.length - 1]?.stage ?? fallback;
}

/**
 * Stage that applied at `at` — last history entry with `changedAt <= at`.
 * If `at` is before the first entry, returns that first entry's stage.
 */
export function getContactStageAt(
  history: ContactStageEntry[] | undefined,
  at: Date,
  fallbackStage: ContactStage = "prospect",
): ContactStage {
  const sorted = normalizeContactStageHistory(history, fallbackStage, at);
  const atMs = at.getTime();
  let result = sorted[0].stage;
  for (const entry of sorted) {
    if (entry.changedAt.getTime() <= atMs) {
      result = entry.stage;
    } else {
      break;
    }
  }
  return result;
}

/**
 * Appends a stage change when it differs from the latest entry. Returns a new
 * array sorted oldest-first.
 */
export function appendContactStageHistory(
  history: ContactStageEntry[] | undefined,
  stage: ContactStage,
  changedAt: Date,
  changedByUserId?: string,
): ContactStageEntry[] {
  const sorted = sortByChangedAtAsc(history ?? []);
  const latest = sorted[sorted.length - 1];
  if (latest?.stage === stage) return sorted;
  return [
    ...sorted,
    {
      stage,
      changedAt,
      ...(changedByUserId ? { changedByUserId } : {}),
    },
  ];
}
